<template>
  <MetaTags v-if="metaInfo" :meta-info="metaInfo" />
  <error-message v-if="errorPageData" :button-text="errorPageData.backButtonText" :link-url="errorPageData.returnUrl">
    {{ errorPageData.message }}
  </error-message>
</template>
<script lang="ts">
import MetaTags from '@/app/components/MetaTags.vue';
import * as actions from '@/app/store/action-types';
import config from '@/config';
import { useSSRContext } from 'vue';
import type { SSRContext } from '@/types/ssr-render.type';
import * as data from '@/app/store/modules/common/data';
import ErrorMessage from '@/app/components/error-message/error-message.vue';
import isSSR from '@/utils/isSSR';
import { useRoute } from 'vue-router';
import { ErrorContextSchema } from '@/schemas/errorSchema';
import logger from '@/app/logger';
import utils from '@/app/utils/utils';

export default {
  setup() {
    // Get error context from SSR context or route query
    const route = useRoute();
    const result = ErrorContextSchema.safeParse(isSSR ? useSSRContext<SSRContext>() : {});
    if (result.error) {
      logger.error('Failed to parse SSR Context: ', result.error);
    }
    let ctx = result.success ? result.data : {};
    if (!ctx || Object.keys(ctx).length === 0) {
      const result = ErrorContextSchema.safeParse(route.query);
      if (result.error) {
        logger.info('Failed to parse error context from route query', result.error);
      }
      ctx = result.success ? result.data : { errorCode: 400 };
    }

    // Get error page data
    const pageName =
      ctx?.errorPageName && Object.values(config.PAGE_NAMES).includes(ctx.errorPageName)
        ? ctx.errorPageName
        : 'defaultMessages';
    const pageMessages = data.ERROR_MESSAGES[pageName as keyof typeof data.ERROR_MESSAGES];
    const code = ctx?.errorCode && data.ERROR_CODES.includes(ctx.errorCode) ? ctx.errorCode : 404;
    const content = pageMessages[code as keyof typeof pageMessages];
    const targetUrl = ctx?.targetUrl && decodeURI(ctx.targetUrl);

    const errorPageData = {
      errorCode: code,
      message: content.message,
      backButtonText: content.backButtonText ?? 'Takaisin',
      returnUrl: content.returnUrl,
      targetUrl,
    };
    return { errorPageData };
  },
  name: 'error',
  computed: {
    metaInfo() {
      return {
        title: 'OP Koti - Virhe',
        meta: utils.getMetaTagsForPage('OP Koti - Virhe', 'OP Koti - Virhe', ''),
      };
    },
  },
  mounted() {
    if (this.$route.query.errorCode && this.$route.query.errorPageName) {
      this.$store.dispatch(actions.SET_ERROR_PAGE_DATA, {
        errorCode: this.$route.query.errorCode || 404,
        errorPageName: this.$route.query.errorPageName || 'virhe',
        targetUrl: decodeURI(this.$route.query.targetUrl) || decodeURI(this.errorPageData.targetUrl ?? ''),
      });
    }
    let target = this.$route.query.targetUrl
      ? decodeURI(this.$route.query.targetUrl)
      : decodeURI(this.errorPageData.targetUrl ?? '');
    if (target !== 'undefined') {
      target = target.replace(/\//g, ':').substring(1);
    } else {
      target = 'virhe';
    }
    const code = this.$route.query.errorCode || this.errorPageData.errorCode;
    // convert URL slashes to semicolons, eliminate first semicolon to make value understandable to adobe...
    void this.$store.dispatch(actions.analytics.set, { pageName: target, eVar14: code });
    void this.$store.dispatch(actions.analytics.track, config.analytics.eventTypes.pageView);
    void this.$store.dispatch(actions.analytics.track, config.analytics.eventTypes.error);
  },
  components: {
    ErrorMessage,
    MetaTags,
  },
};
</script>
